import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Send,
  XCircle,
  CheckCircle,
  Loader2,
  Package,
  Users,
  Calendar,
  FileText,
  BarChart3,
  Building2,
  Mail,
  AlertCircle,
} from 'lucide-react';
import { quoteRequestsApi, handleApiError } from '../lib/api';

interface QuoteRequestItem {
  id: string;
  itemNumber: number;
  description: string;
  specifications?: string;
  quantity: number;
  unit: string;
  requiredBrand?: string;
  notes?: string;
}

interface Supplier {
  id: string;
  name: string;
  email: string;
}

interface QuoteRequestSupplier {
  id: string;
  supplierId: string;
  status: string;
  sentAt?: string;
  supplier: Supplier;
}

interface Quote {
  id: string;
  quoteNumber?: string;
  total?: number;
  currency: string;
  receivedAt: string;
  supplier: Supplier;
  _count: {
    items: number;
    attachments: number;
  };
}

interface QuoteRequest {
  id: string;
  code: string;
  title: string;
  description?: string;
  projectName?: string;
  requiredByDate?: string;
  status: string;
  sentAt?: string;
  closedAt?: string;
  createdAt: string;
  creator: {
    id: string;
    name: string;
    email: string;
  };
  items: QuoteRequestItem[];
  suppliers: QuoteRequestSupplier[];
  quotes: Quote[];
}

const statusConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  DRAFT: { label: 'Borrador', color: 'text-slate-400', bgColor: 'bg-slate-500/10' },
  SENT: { label: 'Enviada', color: 'text-blue-400', bgColor: 'bg-blue-500/10' },
  PARTIALLY_QUOTED: { label: 'Parcialmente Cotizada', color: 'text-yellow-400', bgColor: 'bg-yellow-500/10' },
  FULLY_QUOTED: { label: 'Completamente Cotizada', color: 'text-green-400', bgColor: 'bg-green-500/10' },
  CLOSED: { label: 'Cerrada', color: 'text-purple-400', bgColor: 'bg-purple-500/10' },
  CANCELLED: { label: 'Cancelada', color: 'text-red-400', bgColor: 'bg-red-500/10' },
};

const supplierStatusConfig: Record<string, { label: string; color: string }> = {
  PENDING: { label: 'Pendiente', color: 'text-slate-400' },
  SENT: { label: 'Enviado', color: 'text-blue-400' },
  DELIVERED: { label: 'Entregado', color: 'text-cyan-400' },
  OPENED: { label: 'Abierto', color: 'text-yellow-400' },
  RESPONDED: { label: 'Respondido', color: 'text-green-400' },
  NO_RESPONSE: { label: 'Sin respuesta', color: 'text-red-400' },
};

export function QuotationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [quoteRequest, setQuoteRequest] = useState<QuoteRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchQuoteRequest();
    }
  }, [id]);

  const fetchQuoteRequest = async () => {
    try {
      setIsLoading(true);
      const response = await quoteRequestsApi.get(id!);
      setQuoteRequest(response.data.data?.quoteRequest || null);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    if (!quoteRequest) return;

    if (!confirm('¿Enviar la solicitud de cotizacion a los proveedores seleccionados?')) return;

    setActionLoading('send');
    try {
      await quoteRequestsApi.send(quoteRequest.id);
      fetchQuoteRequest();
    } catch (err) {
      alert(handleApiError(err));
    } finally {
      setActionLoading(null);
    }
  };

  const handleClose = async () => {
    if (!quoteRequest) return;

    if (!confirm('¿Cerrar esta solicitud de cotizacion?')) return;

    setActionLoading('close');
    try {
      await quoteRequestsApi.close(quoteRequest.id);
      fetchQuoteRequest();
    } catch (err) {
      alert(handleApiError(err));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!quoteRequest) return;

    if (!confirm('¿Cancelar esta solicitud de cotizacion?')) return;

    setActionLoading('cancel');
    try {
      await quoteRequestsApi.cancel(quoteRequest.id);
      fetchQuoteRequest();
    } catch (err) {
      alert(handleApiError(err));
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number, currency: string = 'COP') => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (error || !quoteRequest) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <AlertCircle className="w-12 h-12 text-red-400" />
        <p className="text-slate-400">{error || 'Cotizacion no encontrada'}</p>
        <button
          onClick={() => navigate('/quotations')}
          className="text-emerald-400 hover:text-emerald-300"
        >
          Volver a cotizaciones
        </button>
      </div>
    );
  }

  const status = statusConfig[quoteRequest.status] || statusConfig.DRAFT;
  const canSend = quoteRequest.status === 'DRAFT' && quoteRequest.items.length > 0 && quoteRequest.suppliers.length > 0;
  const canClose = ['SENT', 'PARTIALLY_QUOTED', 'FULLY_QUOTED'].includes(quoteRequest.status);
  const canCancel = quoteRequest.status !== 'CLOSED' && quoteRequest.status !== 'CANCELLED';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate('/quotations')}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span className="text-emerald-400 font-mono">{quoteRequest.code}</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${status.bgColor} ${status.color}`}>
                {status.label}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-white">{quoteRequest.title}</h1>
            {quoteRequest.projectName && (
              <p className="text-slate-400 mt-1">Proyecto: {quoteRequest.projectName}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {canSend && (
            <button
              onClick={handleSend}
              disabled={actionLoading === 'send'}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50"
            >
              {actionLoading === 'send' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Enviar a Proveedores
            </button>
          )}

          {quoteRequest.quotes.length > 0 && (
            <button
              onClick={() => navigate(`/quotations/${quoteRequest.id}/compare`)}
              className="flex items-center gap-2 px-4 py-2 bg-inntea-500 text-white rounded-lg hover:bg-inntea-600 transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              Ver Comparativa
            </button>
          )}

          {canClose && (
            <button
              onClick={handleClose}
              disabled={actionLoading === 'close'}
              className="flex items-center gap-2 px-4 py-2 bg-inntea-600 text-white rounded-lg hover:bg-inntea-700 transition-colors disabled:opacity-50"
            >
              {actionLoading === 'close' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Cerrar
            </button>
          )}

          {canCancel && (
            <button
              onClick={handleCancel}
              disabled={actionLoading === 'cancel'}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors disabled:opacity-50"
            >
              {actionLoading === 'cancel' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4" />
              )}
              Cancelar
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{quoteRequest.items.length}</p>
              <p className="text-sm text-slate-400">Items</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{quoteRequest.suppliers.length}</p>
              <p className="text-sm text-slate-400">Proveedores</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{quoteRequest.quotes.length}</p>
              <p className="text-sm text-slate-400">Respuestas</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-white">
                {quoteRequest.requiredByDate
                  ? formatDate(quoteRequest.requiredByDate)
                  : 'Sin fecha'}
              </p>
              <p className="text-sm text-slate-400">Fecha limite</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Items */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Items a Cotizar</h2>
          <div className="space-y-3">
            {quoteRequest.items.map((item) => (
              <div
                key={item.id}
                className="bg-slate-800/50 rounded-lg p-3 border border-slate-700"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-emerald-400">
                        #{item.itemNumber}
                      </span>
                      <span className="text-sm text-white">{item.description}</span>
                    </div>
                    {item.specifications && (
                      <p className="text-xs text-slate-400 mt-1">{item.specifications}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-white">
                      {item.quantity} {item.unit}
                    </p>
                    {item.requiredBrand && (
                      <p className="text-xs text-slate-400">{item.requiredBrand}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Suppliers */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Proveedores</h2>
          {quoteRequest.suppliers.length === 0 ? (
            <p className="text-slate-500 text-sm">No hay proveedores asignados</p>
          ) : (
            <div className="space-y-3">
              {quoteRequest.suppliers.map((qs) => {
                const supplierStatus = supplierStatusConfig[qs.status] || supplierStatusConfig.PENDING;
                const hasQuote = quoteRequest.quotes.some(
                  (q) => q.supplier.id === qs.supplierId
                );

                return (
                  <div
                    key={qs.id}
                    className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3 border border-slate-700"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-slate-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{qs.supplier.name}</p>
                        <p className="text-xs text-slate-400 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {qs.supplier.email}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs font-medium ${supplierStatus.color}`}>
                        {supplierStatus.label}
                      </span>
                      {hasQuote && (
                        <p className="text-xs text-green-400 mt-1">Cotizo</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Received Quotes */}
      {quoteRequest.quotes.length > 0 && (
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Cotizaciones Recibidas</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {quoteRequest.quotes.map((quote) => (
              <div
                key={quote.id}
                className="bg-slate-800/50 rounded-lg p-4 border border-slate-700"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-emerald-400" />
                    <span className="font-medium text-white">{quote.supplier.name}</span>
                  </div>
                  {quote.quoteNumber && (
                    <span className="text-xs text-slate-400">#{quote.quoteNumber}</span>
                  )}
                </div>
                <div className="space-y-2">
                  {quote.total && (
                    <p className="text-lg font-bold text-emerald-400">
                      {formatCurrency(quote.total, quote.currency)}
                    </p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span>{quote._count.items} items</span>
                    <span>{quote._count.attachments} adjuntos</span>
                  </div>
                  <p className="text-xs text-slate-500">
                    Recibida: {formatDate(quote.receivedAt)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Description */}
      {quoteRequest.description && (
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-2">Descripcion</h2>
          <p className="text-slate-400 whitespace-pre-wrap">{quoteRequest.description}</p>
        </div>
      )}

      {/* Metadata */}
      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
        <span>Creado por: {quoteRequest.creator.name}</span>
        <span>Fecha: {formatDate(quoteRequest.createdAt)}</span>
        {quoteRequest.sentAt && <span>Enviada: {formatDate(quoteRequest.sentAt)}</span>}
        {quoteRequest.closedAt && <span>Cerrada: {formatDate(quoteRequest.closedAt)}</span>}
      </div>
    </div>
  );
}
